import { AxiosRequestConfig } from 'axios';
import { DEFAULT_STORE_CODE, STORE_COOKIE_NAME } from '../constants/store';
import { TRIAL_FREQUENCIES } from '../constants/code';
import { ProductForProductPageType } from '../types/ProductPage';

/** Caratteri speciali che il motore di ricerca (sintassi Lucene/Solr) interpreta come operatori: vanno
 * preceduti da backslash per essere trattati come testo letterale nella query di autocomplete. */
const SEARCH_SPECIAL_CHARS_REGEX = /([+\-&|!(){}\[\]^"~*?:/])/g;

export const escapeSearchSpecialChars = (term: string): string => {
  return term?.replace(SEARCH_SPECIAL_CHARS_REGEX, '\\$1') ?? term;
}

/**
 * Extracts the store code from the request cookies, matching the cookie NAME exactly
 * (not a substring match on the raw "name=value" cookie text) to avoid picking the wrong
 * store cookie when another cookie's raw content happens to contain the same string.
 */
export const getStoreCodeFromCookies = (req, defaultStoreCode) => {
  try {
    const storeCookie = req.headers.cookie
      .split(';')
      .map(el => el.trim())
      .find(el => el.slice(0, el.indexOf('=')) === STORE_COOKIE_NAME);
    return storeCookie ? storeCookie.slice(storeCookie.indexOf('=') + 1) : defaultStoreCode;
  } catch (e) {
    return defaultStoreCode;
  }
};

export const getStoreCodeForRequests = req => {
  let storeCode = req.headers.store ?? DEFAULT_STORE_CODE;
  try {
    storeCode = getStoreCodeFromCookies(req, storeCode)
  } catch (e) { }

  return storeCode;
}


export const appendStylesFromText = (cssText, media = 'screen') => {
  if (process.client && document) {
    const el = document.createElement('style');
    // WebKit hack
    el.append(document.createTextNode(''));
    el.type = 'text/css';
    el.media = media;
    el.innerHTML = cssText;
    document.head.append(el);
  }
};

/** This function removes all empty entries (null or empty string)  */
// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
export const removeEmptyValuesFromObject = (obj) => Object.fromEntries(Object.keys(obj)
  .filter((k) => obj[k] != null && obj[k] != '')
  .map((k) => [k, obj[k]]));

const valueToHex = (c) => {
  const hex = Number.parseInt(c as string).toString(16);
  return hex.length == 1 ? `0${hex}` : hex;
};

export const rgbaToHex = (rgba) => {
  const [r, g, b, a] = rgba.replace(/rgba?\(|\)|\s/g, '').split(',');
  return {
    color: `#${valueToHex(r)}${valueToHex(g)}${valueToHex(b)}`,
    opacityPercentage: Number.parseInt(Math.round(a * 100).toString()),
  };
};

const calculateTintAndShade = (
  hexColor, // using #663399 as an example
  percentage = 0.1 // using 10% as an example
) => {
  const r = parseInt(hexColor.slice(1, 3), 16); // r = 102
  const g = parseInt(hexColor.slice(3, 5), 16); // g = 51
  const b = parseInt(hexColor.slice(5, 7), 16); // b = 153

  /* 
     From this part, we are using our two formulas
     in this case, here is the formula for tint,
     please be aware that we are performing two validations
     we are using Math.min to set the max level of tint to 255,
     so we don't get values like 280 ;)
     also, we have the Math.round so we don't have values like 243.2
     both validations apply for both tint and shade as you can see */
  const tintR = Math.round(Math.min(255, r + (255 - r) * percentage)); // 117
  const tintG = Math.round(Math.min(255, g + (255 - g) * percentage)); // 71
  const tintB = Math.round(Math.min(255, b + (255 - b) * percentage)); // 163


  const shadeR = Math.round(Math.max(0, r - r * percentage)); // 92
  const shadeG = Math.round(Math.max(0, g - g * percentage)); // 46
  const shadeB = Math.round(Math.max(0, b - b * percentage)); // 138


  /* 
     Now with all the values calculated, the only missing stuff is 
     getting our color back to hexadecimal, to achieve that, we are going
     to perform a toString(16) on each value, so we get the hex value
     for each color, and then we just append each value together and voilà!*/
  return {
    tint: {
      r: tintR,
      g: tintG,
      b: tintB,
      hex:
        '#' +
        [tintR, tintG, tintB]
          .map(x => x.toString(16).padStart(2, '0'))
          .join(''), // #7547a3 
    },
    shade: {
      r: shadeR,
      g: shadeG,
      b: shadeB,
      hex:
        '#' +
        [shadeR, shadeG, shadeB]
          .map(x => x.toString(16).padStart(2, '0'))
          .join(''), // #5c2e8a 
    },
  };
};

export const DarkenColor = (hexColor, percentage) => {
  return calculateTintAndShade(hexColor, percentage).shade.hex
}

/**
 * In the css_classes magento field the column char is not allowed
 * We can use triple underscore to manage this behavior.
 * In order to manage tailwind media classes like md:bg-red lg:bg-white
 * we can use md___bg-red lg___bg-white and the replace ___ with : to use them in html
 */
export const parseClasses = (text: string) => {
  const replacements: { [key: string]: string } = {
    '_open_square_bracket_': '[',
    '_osb_': '[',
    '_csb_': ']',
    '_close_square_bracket_': ']',
    '_open_round_bracket_': '(',
    '_close_round_bracket_': ')',
    '_orb_': '(',
    '_crb_': ')',
    '!solepro_hidden': '!hidden',
    'solepro_hidden': 'hidden',
    '_slash_': '/',
    '_important_': '!',
    '_imp_': '!',
    '_percentage_': '%',
    '_AND_': '&',
    '_STAR_': '*',
    '_IN_': '>',
    '___': ':',
    '_DOT_': '.',
    '_S_': '#'
  };

  // Creiamo una regex che combini tutte le chiavi (le stringhe da sostituire)
  // in un unico pattern. Usiamo 'g' per la sostituzione globale (tutte le occorrenze)
  // e 'gi' per rendere la regex case-insensitive se necessario, ma in questo caso
  // non sembra esserlo dalle tue stringhe originali.
  // Escapiamo i caratteri speciali per la regex come '!', '(', ')' ecc.
  const pattern = new RegExp(Object.keys(replacements).map(key =>
    key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  ).join('|'), 'g');




  return text.replace(pattern, (match) => replacements[match]);
};

export const getAxiosConfigs = (app, config) => {

  let axiosConfigs: AxiosRequestConfig = {
    headers: {
      Store: DEFAULT_STORE_CODE
    }
  }

  if (app.$config.soleEnv == 'local') {
    axiosConfigs.baseURL = `http://${app.$config.localURL}`
  } else {
    axiosConfigs.baseURL = `${app.$config.soleproDomain}`
  }

  return {
    ...axiosConfigs
  }

}

export const calcolaBoxInWidth = (spazioTotale) => {
  // Larghezza di un singolo box
  const larghezzaBox = 340;
  // Larghezza dello spazio tra i box
  const spazioTraBox = 20;

  // L'unità di calcolo è la larghezza di un box più lo spazio successivo
  // Ad eccezione del primo box
  const unita = larghezzaBox + spazioTraBox;

  // Sottraiamo lo spazio occupato dal primo box
  const spazioRimanente = spazioTotale - larghezzaBox;

  // Se lo spazio totale non è sufficiente neanche per il primo box, non ci sono box.
  if (spazioRimanente < 0) {
    return 0;
  }

  // Calcoliamo quanti box "aggiuntivi" (con il loro spazio) ci stanno
  const boxAggiuntivi = Math.floor(spazioRimanente / unita);

  // Il numero totale di box è 1 (il primo) + i box aggiuntivi
  const totaleBox = 1 + boxAggiuntivi;

  return totaleBox;
}


export const isElementInViewport = (element) => {
  // getBoundingClientRect() ci dà la posizione (top, bottom, left, right)
  // dell'elemento rispetto al viewport.
  const rect = element.getBoundingClientRect();

  // Controlliamo se una parte dell'elemento è all'interno del viewport.
  // L'elemento è visibile se:
  // 1. Il suo bordo superiore (rect.top) è sopra o all'interno del fondo del viewport (window.innerHeight)
  // 2. E il suo bordo inferiore (rect.bottom) è sotto o all'interno dell'alto del viewport (0)

  // Inoltre, è necessario che i valori non siano 0 per un elemento senza dimensioni.
  return (
    rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.left < (window.innerWidth || document.documentElement.clientWidth)
  );
}

export const replaceStaticWithCdnInCss = (property, htmlString, IMAGE_STATIC_REPLACEMENT, IMAGE_CDN_REPLACEMENT) => {
  /**
   * Sostituisce IMAGE_STATIC_REPLACEMENT con IMAGE_CDN_REPLACEMENT 
   * solo all'interno dei valori delle proprietà CSS 'background-image:'.
   *
   * @param {string} property - Proprietà css da individuare
   * @param {string} htmlString - La stringa HTML su cui eseguire la sostituzione.
   * @returns {string} La stringa HTML modificata.
   */

  const regex = new RegExp(`(${property.trim()}:)(.*?)(;)`, 'gis');

  return htmlString.replace(regex, (match, prefix, value, suffix) => {
    const newValue = value.replace(IMAGE_STATIC_REPLACEMENT, IMAGE_CDN_REPLACEMENT);
    return prefix + newValue + suffix;
  });
}

export const replaceStaticWithCdnInImages = (htmlString, IMAGE_STATIC_REPLACEMENT, IMAGE_CDN_REPLACEMENT) => {
  /**
   * Sostituisce 'IMAGE_STATIC_REPLACEMENT' con 'IMAGE_CDN_REPLACEMENT' solo all'interno dell'attributo specificato.
   * La sostituzione avviene all'interno del contesto di 'includeTag' ed esclude 
   * il contesto di 'excludeTag' (se il target è al suo interno).
   * * Questo approccio è ottimizzato per la sostituzione su attributi (come srcset) 
   * che possono contenere valori su più righe.
   */

  // Cattura tutti i blocchi <video>...</video> per *saltarli* dopo.
  const excludeRegex = new RegExp(`(<video[^>]*>)([\\s\\S]*?)(<\/video>)`, 'gi');

  // 2. Regex per l'INCLUSIONE: Blocchi <figure> o <picture>
  const includeRegex = new RegExp(`(<figure[^>]*>)([\\s\\S]*?)(<\/figure>)`, 'gi');

  // 3. Regex interna per trovare l'attributo src o srcset
  const targetAttrRegex = new RegExp(`(src)(\\s*=\\s*)(["'])(.*?)(["'])`, 'gis');

  // --- LOGICA DI ESCLUSIONE: Saltare i blocchi <video> ---
  const EXCLUSION_PLACEHOLDER = "@@@EXCLUDED_BLOCK@@@";
  let processedHtml = htmlString.replace(excludeRegex, EXCLUSION_PLACEHOLDER);

  // --- LOGICA DI INCLUSIONE E SOSTITUZIONE ---
  // Processiamo solo i blocchi del tag di inclusione (picture o figure).
  processedHtml = processedHtml.replace(includeRegex, (match, openTag, content, closeTag) => {

    // Eseguiamo la sostituzione interna dell'attributo target (src o srcset)
    const newContent = content.replace(targetAttrRegex, (srcMatch, attrName, equals, quoteOpen, attrValue, quoteClose) => {

      let newAttrValue = attrValue;

      // Usiamo il robusto split().join() per la sostituzione globale e sicura
      if (attrValue.includes(IMAGE_STATIC_REPLACEMENT)) {
        newAttrValue = attrValue.split(IMAGE_STATIC_REPLACEMENT).join(IMAGE_CDN_REPLACEMENT);
      }

      // Ricostruiamo l'attributo completo
      return attrName + equals + quoteOpen + newAttrValue + quoteClose;
    });

    // Ricostruiamo l'intero blocco di inclusione
    return openTag + newContent + closeTag;
  });

  // --- LOGICA FINALE: Ripristino dei blocchi esclusi ---
  processedHtml = processedHtml.replace(new RegExp(EXCLUSION_PLACEHOLDER, 'g'));


  // Sostituzione universale solo sull'attributo target
  let finalHtml = htmlString.replace(targetAttrRegex, (srcMatch, attrName, equals, quoteOpen, attrValue, quoteClose) => {
    let newAttrValue = attrValue;

    if (attrValue.includes(IMAGE_STATIC_REPLACEMENT)) {
      newAttrValue = attrValue.split(IMAGE_STATIC_REPLACEMENT).join(IMAGE_CDN_REPLACEMENT);
    }
    return attrName + equals + quoteOpen + newAttrValue + quoteClose;
  });

  // Annulla la sostituzione solo all'interno dei blocchi esclusi
  const excludeContentRegex = new RegExp(`(<video[^>]*>)([\\s\\S]*?)(<\/video>)`, 'gi');

  finalHtml = finalHtml.replace(excludeContentRegex, (match, openTag, content, closeTag) => {

    // All'interno del blocco <video>, annulla la sostituzione
    const revertedContent = content.replace(targetAttrRegex, (srcMatch, attrName, equals, quoteOpen, attrValue, quoteClose) => {
      let revertedAttrValue = attrValue;

      // Ri-sostituisci IMAGE_CDN_REPLACEMENT con IMAGE_STATIC_REPLACEMENT
      if (attrValue.includes(IMAGE_CDN_REPLACEMENT)) {
        revertedAttrValue = attrValue.split(IMAGE_CDN_REPLACEMENT).join(IMAGE_STATIC_REPLACEMENT);
      }
      return attrName + equals + quoteOpen + revertedAttrValue + quoteClose;
    });

    return openTag + revertedContent + closeTag;
  });

  return finalHtml;
}

export const replaceStaticWithCdnFromImageUrls = (url, IMAGE_STATIC_REPLACEMENT, IMAGE_CDN_REPLACEMENT) => {
  if (!url) return url;
  let newUrl = url;

  if (url.includes(IMAGE_STATIC_REPLACEMENT)) {
    newUrl = url.split(IMAGE_STATIC_REPLACEMENT).join(IMAGE_CDN_REPLACEMENT);
  }

  return newUrl;
};



export const getClassLabel = (label): string => {
  const sanitizeString = label?.toLowerCase().replace('à', 'a').replace(/[^a-zA-Z ]/g, "").replace(/\s+/g, "-") ?? '';
  return sanitizeString;
}

export const cleanButtonsClasses = btn => {
  btn.classList.add('!mx-0')
  const text = Array.from(btn.classList).join(' ');
  let color, hoverColor;
  if (text.includes('btnColor-')) {
    const regex = /btnColor-([a-fA-F0-9]+)/;
    const match = text.match(regex);
    if (match) {
      color = match[1]
    }
  }
  if (text.includes('btnColor-')) {
    const regex = /btnText-([a-fA-F0-9]+)/;
    const match = text.match(regex);
    if (match) {
      hoverColor = match[1]
    }
  }


  if (color || hoverColor) {
    color = color ?? '000000'
    if (text.includes('pagebuilder-button-primary')) {
      btn.classList.add(`!bg-[#${color}]`)
      btn.classList.add(`!border-[#${color}]`)
      hoverColor ? btn.classList.add(`!text-[#${hoverColor}]`) : null
    } else if (text.includes('pagebuilder-button-secondary')) {
      hoverColor = hoverColor ?? 'FFFFFF'
      btn.classList.add(`!text-[#${color}]`)
      btn.classList.add(`!border-[#${color}]`)
      btn.classList.add(`hover:!bg-[#${color}]`)
      btn.classList.add(`hover:!text-[#${hoverColor}]`)
    } else {
      btn.classList.add(`!text-[#${color}]`)
      btn.classList.add(`after:!border-[#${color}]`)
      btn.classList.add(`after:!bg-[#${color}]`)
    }
  }
  return btn
}

/**
 * Recupera il valore di una proprietà CSS da un tag <style> locale.
 * 
 * */
export const getPropertyValueFromStyleTag = (selector, propertyName) => {
  // 1. Accediamo a tutti i fogli di stile della pagina.
  // document.styleSheets contiene sia i tag <style> che i file .css esterni.
  const stylesheets = Array.from(document.styleSheets);

  // Trasformiamo la proprietà in kebab-case (es. da backgroundColor a background-color)
  const kebabProperty = propertyName.replace(/([A-Z])/g, "-$1").toLowerCase();

  for (const sheet of stylesheets) {
    try {
      // 2. Filtriamo per considerare solo i tag <style> locali (HTMLStyleElement)
      // Questo evita errori di sicurezza (CORS) con file CSS esterni.
      if (sheet.ownerNode instanceof HTMLStyleElement) {
        const rules = sheet.cssRules || sheet.rules;

        // Iteriamo le regole dall'ultima alla prima (per rispettare la priorità CSS)
        for (let i = rules.length - 1; i >= 0; i--) {
          const rule = rules[i];

          // 3. Verifichiamo se è una regola standard e se il selettore corrisponde
          if (rule instanceof CSSStyleRule && rule.selectorText === selector) {
            console.log('rule.selectorText:::', rule.selectorText)
            console.log('rule.style:::', rule.style)
            const value = rule.style.getPropertyValue(kebabProperty).trim();

            // Se troviamo il valore, lo restituiamo immediatamente
            if (value) return value;
          }
        }
      }
    } catch (e) {
      // Se un foglio di stile è inaccessibile, passiamo al prossimo
      console.warn("Impossibile leggere le regole di un foglio di stile:", e);
      continue;
    }
  }

  return null; // Ritorna null se non trova nulla
};

export const DEEP = 10;

export const findElementByContentType = (data, type, counter = 0) => {
  if (counter > DEEP) return null; // Limite di profondità raggiunto, evitiamo ricorsioni infinite
  counter++;
  // 1. Se data è un array, cicliamo su ogni elemento
  if (Array.isArray(data)) {
    for (let item of data) {
      let found = findElementByContentType(item, type, counter);
      if (found) return found; // Se trovato, lo restituiamo subito
    }
  }
  // 2. Se data è un oggetto, controlliamo se è quello giusto
  else if (typeof data === 'object' && data !== null) {
    if (data.contentType === type) {
      return data; // Elemento trovato!
    }

    // Se non è questo, cerchiamo nelle sue proprietà (es. 'children' o 'items')
    for (let key in data) {
      // DOMContent è il nodo DOM jsdom grezzo (origin opaco): scendendoci dentro
      // il for...in arriva fino a window.localStorage e lancia SecurityError
      if (key === 'DOMContent') continue;
      if (typeof data[key] === 'object') {
        let found = findElementByContentType(data[key], type, counter);
        if (found) return found;
      }
    }
  }

  return null; // Se non troviamo nulla in questo ramo
}

/**
 * <h3 data-content-type="heading" data-appearance="default" data-show-in-preview="true" data-show-online="true" data-element="main" id="id-1782206410399">Formazione</h3> 
 * returns
 * "h3"
 */
export const getTagFromString = (str) => {
  const regex = /<([a-z1-6]+)/i;
  const match = str.match(regex);
  if (match) {
    const nomeTag = match[1]; // Il gruppo 1 contiene il nome del tag
    return nomeTag
  } else return 'span'
}

export const isCurrentTrial = (product: ProductForProductPageType, frequency: string) => {
  const isLowFrequency = product.paymentFrequencies[0] == frequency;
  const isHighFrequency = product.paymentFrequencies?.[1] == frequency;
  return !!product.trial_frequency ?  product.isTrial && 
    (
      product.trial_frequency == TRIAL_FREQUENCIES.both ||
      (isLowFrequency && product.trial_frequency == TRIAL_FREQUENCIES.low) ||
      (isHighFrequency && product.trial_frequency == TRIAL_FREQUENCIES.high)
    ) : product.isTrial && isLowFrequency;
}